import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../lib/cn';

type Variant = 'primary' | 'dark' | 'ghost' | 'outline' | 'danger';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  /** Stretch to fill the container — common for the front-desk primary action. */
  block?: boolean;
}

const base =
  'inline-flex items-center justify-center gap-2 rounded-pill font-medium ' +
  'transition-[transform,background-color,box-shadow] duration-200 ease-spring ' +
  'active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 ' +
  'whitespace-nowrap select-none';

const variants: Record<Variant, string> = {
  primary: 'bg-accent text-accent-ink hover:bg-accent-strong shadow-[0_8px_20px_-10px_rgba(170,200,30,0.7)]',
  dark: 'bg-dark text-on-dark hover:bg-dark-soft',
  ghost: 'bg-transparent text-ink hover:bg-surface',
  outline: 'bg-elevated text-ink border border-line hover:border-line-strong hover:bg-card',
  danger: 'bg-bad text-white hover:brightness-110',
};

const sizes: Record<Size, string> = {
  sm: 'h-9 px-4 text-sm',
  md: 'h-touch px-5 text-[0.9375rem]', // 44px — front-desk default
  lg: 'h-12 px-7 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'primary', size = 'md', loading = false, block = false, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(base, variants[variant], sizes[size], block && 'w-full', className)}
      {...props}
    >
      {loading && <Loader2 className="size-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});
